import { useState, useEffect, useCallback } from "react";
import { Calendar, ChevronDown, ChevronUp, RefreshCw, Plus, User, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import ParentHeader from "@/components/common/ParentHeader";
import AdminHeader from "@/components/common/AdminHeader";
import ApplyLeaveModal from "@/components/modals/ApplyLeaveModal";
import AdminLeavesView from "./AdminLeavesView";
import { getParentLeaves, cancelLeaveRequest, cancelAllLeaveRequests } from "@/services/parent";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fontSize, fontWeight, textColor } from "@/theme";

interface LeaveItem {
  id?: number;
  Date: string;
  Child_Id?: string;
  Child_Name: string;
  Status: string;
}

interface LeaveGroup {
  id?: number;
  Leave_Id: string;
  Username: string;
  Subscription_Code: string;
  Created_At: string;
  Leaves: LeaveItem[];
}

function parseLeaveDateStr(leaveDate: string): string {
  const ddmmyyyy = leaveDate.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  return ddmmyyyy ? `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}` : leaveDate;
}

function canCancelLeave(leaveDate: string): boolean {
  if (!leaveDate) return false;
  const deadline = new Date(`${parseLeaveDateStr(leaveDate)}T09:00:00`);
  return !isNaN(deadline.getTime()) && new Date() < deadline;
}

function isDatePast(leaveDate: string): boolean {
  if (!leaveDate) return true;
  const endOfDay = new Date(`${parseLeaveDateStr(leaveDate)}T23:59:59`);
  return !isNaN(endOfDay.getTime()) && new Date() > endOfDay;
}

// True if it's today AND current time is past 1 PM — hide the row entirely
function isTodayAfter1PM(leaveDate: string): boolean {
  const parsed = parseLeaveDateStr(leaveDate);
  const todayStr = new Date().toISOString().split("T")[0];
  return parsed === todayStr && new Date().getHours() >= 13;
}

function statusBadge(status: string) {
  const s = status?.toLowerCase();
  if (s === "confirm") return "bg-green-100 text-green-700 border-green-200";
  if (s === "cancle" || s === "cancelled") return "bg-red-100 text-red-700 border-red-200";
  return "bg-yellow-100 text-yellow-700 border-yellow-200";
}

function statusLabel(status: string) {
  const s = status?.toLowerCase();
  if (s === "confirm") return "Confirmed";
  if (s === "cancle" || s === "cancelled") return "Cancelled";
  return status;
}

function ParentLeavesView() {
  const userDetails = useCurrentUser();
  const username = userDetails?.username || "";

  const [leaves, setLeaves] = useState<LeaveGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [cancellingGroupId, setCancellingGroupId] = useState<string | null>(null);
  const [cancellingLeafIds, setCancellingLeafIds] = useState<Set<number>>(new Set());
  const [selectedLeafIds, setSelectedLeafIds] = useState<Set<number>>(new Set());

  const loadLeaves = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    try {
      const response = await getParentLeaves(username);
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const list: LeaveGroup[] = Array.isArray(data) ? data : data?.results ?? [];
        setLeaves(list);
      }
    } catch (error) {
      console.error("Failed to load leaves:", error);
      // toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  const toggleExpand = (leaveId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(leaveId) ? next.delete(leaveId) : next.add(leaveId);
      return next;
    });
  };

  const toggleLeafSelection = (id: number) => {
    setSelectedLeafIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Cancel selected leaf ids via PATCH /parentleaves/?Id=<id>
  const handleCancelChecked = async (selectableIds: number[]) => {
    const toCancel = selectableIds.filter((id) => selectedLeafIds.has(id));
    if (toCancel.length === 0) return;
    setCancellingLeafIds(new Set(toCancel));
    try {
      await Promise.all(toCancel.map((id) => cancelLeaveRequest(id, { Status: "Cancle" })));
      toast.success(`${toCancel.length} leave entr${toCancel.length > 1 ? "ies" : "y"} cancelled`);
      setSelectedLeafIds((prev) => {
        const next = new Set(prev);
        toCancel.forEach((id) => next.delete(id));
        return next;
      });
      loadLeaves();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Something went wrong while cancelling");
    } finally {
      setCancellingLeafIds(new Set());
    }
  };

  const handleCancelAll = async (group: LeaveGroup) => {
    if (!window.confirm(`Cancel all leaves in request ${group.Leave_Id}?`)) return;
    setCancellingGroupId(group.Leave_Id);
    try {
      const response = await cancelAllLeaveRequests(group.Leave_Id, { Status: "Cancle" });
      if (response.status === 200 || response.status === 204) {
        toast.success("All leaves cancelled");
        loadLeaves();
      } else {
        toast.error("Failed to cancel leave request");
      }
    } catch (error) {
      console.error("Cancel all error:", error);
      toast.error("Something went wrong");
    } finally {
      setCancellingGroupId(null);
    }
  };

  const activeCount = leaves.filter((l) =>
    l.Leaves?.some((li) => li.Status?.toLowerCase() === "confirm")
  ).length;
  const totalDays = leaves.reduce((sum, l) => sum + (l.Leaves?.length ?? 0), 0);

  return (
    <>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`${fontSize['3xl']} ${fontWeight.bold} ${textColor.heading}`}>Leave Management</h1>
            <p className={`${textColor.muted} mt-1 text-sm`}>Request and manage delivery pauses for your children</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadLeaves}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowApplyModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-sm text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              New Request
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-brand-500" />
            </div>
            <div>
              <p className={`text-xs ${textColor.muted} font-medium`}>Total Requests</p>
              <p className={`${fontSize['2xl']} ${fontWeight.bold} ${textColor.heading}`}>{leaves.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className={`text-xs ${textColor.muted} font-medium`}>Active Requests</p>
              <p className={`${fontSize['2xl']} ${fontWeight.bold} text-green-700`}>{activeCount}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className={`text-xs ${textColor.muted} font-medium`}>Total Leave Days</p>
              <p className={`${fontSize['2xl']} ${fontWeight.bold} text-purple-700`}>{totalDays}</p>
            </div>
          </div>
        </div>

        {/* Leave List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto mb-3" />
            <p className={`${textColor.muted} text-sm`}>Loading leave requests…</p>
          </div>
        ) : leaves.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
            <div className="w-20 h-20 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <Calendar className="w-10 h-10 text-brand-400" />
            </div>
            <h3 className={`${fontSize.xl} ${fontWeight.bold} ${textColor.heading} mb-2`}>No Leave Requests</h3>
            <p className={`text-sm ${textColor.muted} mb-6`}>You haven't submitted any leave requests yet</p>
            <button
              onClick={() => setShowApplyModal(true)}
              className="px-6 py-3 bg-brand-500 text-white rounded-xl hover:bg-brand-700 transition-colors font-semibold text-sm"
            >
              + Create Leave Request
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {leaves.map((group) => {
              const isExpanded = expandedIds.has(group.Leave_Id);
              const allCancelled = group.Leaves?.every((l) =>
                ["cancle", "cancelled"].includes(l.Status?.toLowerCase())
              );
              const createdDate = new Date(group.Created_At).toLocaleDateString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
              });

              // Group leaf items by child name
              const byChild: Record<string, LeaveItem[]> = {};
              group.Leaves?.forEach((leaf) => {
                const name = leaf.Child_Name || "Unknown";
                if (!byChild[name]) byChild[name] = [];
                byChild[name].push(leaf);
              });

              const anyLeafCancellable = group.Leaves?.some(
                (l) =>
                  !["cancle", "cancelled"].includes(l.Status?.toLowerCase()) &&
                  !isTodayAfter1PM(l.Date) &&
                  canCancelLeave(l.Date)
              );
              const isCancellingGroup = cancellingGroupId === group.Leave_Id;

              return (
                <div
                  key={group.Leave_Id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  {/* Group Header */}
                  <div className="flex items-start justify-between p-5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className={`${fontWeight.bold} ${textColor.heading} text-sm`}>{group.Leave_Id}</span>
                        {allCancelled ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                            Cancelled
                          </span>
                        ) : (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
                        <span>{createdDate}</span>
                        <span>·</span>
                        <span>{group.Leaves?.length ?? 0} entries</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {anyLeafCancellable && !allCancelled && (
                        <button
                          onClick={() => handleCancelAll(group)}
                          disabled={isCancellingGroup}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          {isCancellingGroup ? "Cancelling…" : "Cancel All"}
                        </button>
                      )}
                      <button
                        onClick={() => toggleExpand(group.Leave_Id)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded — child-wise grouping */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
                      {Object.entries(byChild).map(([childName, childLeaves]) => {
                        // Selectable leaf ids: not cancelled, not past, before 9 AM, has numeric id
                        const selectableIds = childLeaves
                          .filter(
                            (l) =>
                              l.id &&
                              !["cancle", "cancelled"].includes(l.Status?.toLowerCase()) &&
                              !isDatePast(l.Date) &&
                              !isTodayAfter1PM(l.Date) &&
                              canCancelLeave(l.Date)
                          )
                          .map((l) => l.id!);

                        const selectedCount = selectableIds.filter((id) => selectedLeafIds.has(id)).length;
                        const isCancellingChild = selectableIds.some((id) => cancellingLeafIds.has(id));

                        return (
                          <div key={childName} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            {/* Child header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3.5 h-3.5 text-brand-500" />
                                </div>
                                <span className="text-sm font-semibold text-gray-800">{childName}</span>
                                {selectedCount > 0 && (
                                  <span className="text-xs font-medium text-brand-500 bg-brand-50 border border-brand-200 px-1.5 py-0.5 rounded-full">
                                    {selectedCount} selected
                                  </span>
                                )}
                              </div>
                              {selectableIds.length > 0 && (
                                <button
                                  onClick={() => handleCancelChecked(selectableIds)}
                                  disabled={selectedCount === 0 || isCancellingChild}
                                  className="flex items-center gap-1 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <XCircle className="w-3 h-3" />
                                  {isCancellingChild ? "Cancelling…" : "Cancel"}
                                </button>
                              )}
                            </div>

                            {/* Date rows */}
                            <div className="divide-y divide-gray-50">
                              {childLeaves.map((leaf, idx) => {
                                const isCancelled = ["cancle", "cancelled"].includes(leaf.Status?.toLowerCase());
                                const isPast = isDatePast(leaf.Date);
                                const hiddenAfter1PM = !isCancelled && !isPast && isTodayAfter1PM(leaf.Date);

                                if (hiddenAfter1PM) return null;

                                const isSelectable = !!leaf.id && !isCancelled && !isPast && canCancelLeave(leaf.Date);
                                const isContactAdmin = !isCancelled && !isPast && !canCancelLeave(leaf.Date);
                                const isChecked = !!leaf.id && selectedLeafIds.has(leaf.id);

                                return (
                                  <div
                                    key={idx}
                                    className={`flex items-center justify-between px-4 py-3 ${
                                      isSelectable ? "hover:bg-brand-50/40 transition-colors cursor-pointer" : ""
                                    }`}
                                    onClick={() => isSelectable && leaf.id && toggleLeafSelection(leaf.id)}
                                  >
                                    <div className="flex items-center gap-3">
                                      {isSelectable ? (
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => leaf.id && toggleLeafSelection(leaf.id)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-4 h-4 text-brand-500 rounded accent-brand-500 cursor-pointer"
                                        />
                                      ) : (
                                        <div className="w-4 h-4" />
                                      )}
                                      <p className="text-sm text-gray-700">{leaf.Date}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusBadge(leaf.Status)}`}>
                                        {statusLabel(leaf.Status)}
                                      </span>
                                      {isContactAdmin && (
                                        <span className="text-xs text-amber-600 font-medium bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg">
                                          Contact admin
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info note */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <div className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
          </div>
          <p className="text-amber-700 text-sm">
            <strong>Cancellation Policy:</strong> Select specific dates per child and cancel before <strong>9 AM</strong>. After 9 AM, contact your administrator.
            </p>
        </div>
      </main>

      <ApplyLeaveModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onSuccess={() => { setShowApplyModal(false); loadLeaves(); }}
      />
    </>
  );
}

function AdminLeavesPage() {
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="mb-8">
        <h1 className={`${fontSize['3xl']} ${fontWeight.bold} ${textColor.heading}`}>Leave Requests</h1>
        <p className={`${textColor.muted} mt-1 text-sm`}>Monitor and manage all parent leave requests</p>
      </div>
      <AdminLeavesView />
    </main>
  );
}

export default function LeavesComponent() {
  const userDetails = useCurrentUser();
  const role = (userDetails as any)?.role || "";
  const isParent = role === "Parent";
  const Header = isParent ? ParentHeader : AdminHeader;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {isParent ? <ParentLeavesView /> : <AdminLeavesPage />}
    </div>
  );
}
